// 모든 게임 엔티티의 기본 클래스
class Entity {
  constructor(scene, x = 0, y = 0, z = 0, config = {}) {
    this.scene = scene;
    this.model = null;
    this.mixer = null;
    this.position = new THREE.Vector3(x, y, z);

    // 기본 설정
    this.config = config;
    this.isLoaded = false;
    this.isAlive = true;

    // 체력 시스템 (Config 우선 사용)
    this.maxHealth = config.MAX_HEALTH || config.HEALTH || 100;
    this.health = this.maxHealth;

    // 피격 시스템 (Config 우선 사용)
    this.isHit = false;
    this.hitStunEndTime = 0;
    this.hitFlashEndTime = 0;
    this.hitStunDuration = config.HIT_STUN_DURATION || 0.5;
    this.hitFlashDuration = config.HIT_FLASH_DURATION || 0.3;
    this.originalMaterials = new Map();

    // 죽음 시스템 (Config 우선 사용)
    this.isDying = false;
    this.deathStartTime = 0;
    this.deathAnimationDuration = config.DEATH_ANIMATION_DURATION || 1.0;

    // 애니메이션 시스템
    this.animations = {}; // 원본 애니메이션 클립들 저장 (인덱스로 접근)
    this.currentAnimation = null;

    // 공통 애니메이션 액션들 (모든 Entity가 사용)
    this.idleAction = null;
    this.moveAction = null;
    this.aimAction = null;
    this.attackAction = null;
    this.hitAction = null;
    this.deathAction = null;

    // 애니메이션 상태 추적 시스템
    this.allAnimations = []; // 모든 애니메이션 배열 (나중에 초기화)

    // 콜라이더 시스템 (Config 우선 사용)
    this.colliderRadius = config.COLLIDER_RADIUS || 0.5;
    this.environmentObjects = [];

    // 이동 관련 (Config 우선 사용)
    this.moveSpeed = config.MOVE_SPEED || config.SPEED || 1.0;
    this.isMoving = false;

    // 공격 관련 (모든 Entity가 공격 가능) (Config 우선 사용)
    this.isAttacking = false;
    this.lastAttackTime = 0;
    this.attackRange = config.ATTACK_RANGE || 2.0;
    this.attackDamage = config.ATTACK_DAMAGE || 10;
    this.attackCooltime = config.ATTACK_COOLTIME || 1.0;
    this.attackAngle = config.ATTACK_ANGLE || Math.PI / 2; // 기본 90도

    // 모델 관련 (Config 우선 사용)
    this.modelPath = config.MODEL_PATH || null;
    this.scale = config.SCALE || 1.0;

    // 상태 관련 (모든 Entity가 상태를 가짐)
    this.currentState = "IDLE"; // IDLE, MOVING, AIM, ATTACK, HIT, DEATH

    // 강제 idle 상태 관련
    this.isForcedIdle = false; // 강제 idle 상태 (게임 특정 상황에서 사용)

    // 데미지 숫자 표시 시스템
    this.damageNumbers = []; // 활성화된 데미지 숫자들
  }

  // 엔티티 초기화 (서브클래스에서 오버라이드)
  async init() {
    // TODO : 왜 여기서 구현 안함??
    throw new Error("init() must be implemented by subclass");
  }

  // 모델 로딩 (서브클래스에서 구현)
  async loadModel() {
    // TODO : 이것도... 그냥 config에 modelpath있으면 load되면 됨.
    throw new Error("loadModel() must be implemented by subclass");
  }

  // 모델 속성 설정 (그림자, 스케일 등)
  setupModelProperties() {
    if (!this.model) return;

    this.model.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = false;
      }
    });

    // 위치 설정
    this.model.position.copy(this.position);

    // 스케일 설정 (멤버 변수 사용)
    this.model.scale.setScalar(this.scale);
  }

  // 애니메이션 인덱스 가져오기 (서브클래스에서 오버라이드)
  getAnimationIndices() {
    // 기본 구현: 서브클래스에서 반드시 오버라이드해야 함
    throw new Error("getAnimationIndices() must be implemented by subclass");
  }

  // 애니메이션 설정 (모든 Entity 공통)
  setupAnimations(animations) {
    if (!animations || animations.length === 0) return;

    this.mixer = new THREE.AnimationMixer(this.model);

    // 원본 애니메이션 배열 저장 (인덱스로 접근하기 위해)
    this.animations = animations;

    // 서브클래스에서 정의한 인덱스 가져오기
    const indices = this.getAnimationIndices();

    // idle 애니메이션 설정
    if (indices.idle !== undefined && animations[indices.idle]) {
      this.idleAction = this.mixer.clipAction(animations[indices.idle]);
      this.idleAction.setLoop(THREE.LoopRepeat);
    }

    // move 애니메이션 설정
    if (indices.move !== undefined && animations[indices.move]) {
      this.moveAction = this.mixer.clipAction(animations[indices.move]);
      this.moveAction.setLoop(THREE.LoopRepeat);
    }

    // aim 애니메이션 설정 (선택적)
    if (indices.aim !== undefined && animations[indices.aim]) {
      this.aimAction = this.mixer.clipAction(animations[indices.aim]);
      this.aimAction.setLoop(THREE.LoopOnce);
      this.aimAction.clampWhenFinished = true;
    }

    // attack 애니메이션 설정
    if (indices.attack !== undefined && animations[indices.attack]) {
      this.attackAction = this.mixer.clipAction(animations[indices.attack]);
      this.attackAction.setLoop(THREE.LoopOnce);
      this.attackAction.clampWhenFinished = true;

      // 공격 애니메이션 속도를 쿨타임에 맞춰 조정
      const duration = animations[indices.attack].duration;
      this.attackAction.setEffectiveTimeScale(duration / this.attackCooltime);
    }

    // hit 애니메이션 설정
    if (indices.hit !== undefined && animations[indices.hit]) {
      this.hitAction = this.mixer.clipAction(animations[indices.hit]);
      this.hitAction.setLoop(THREE.LoopOnce);
      this.hitAction.clampWhenFinished = true;
    }

    // death 애니메이션 설정
    if (indices.death !== undefined && animations[indices.death]) {
      this.deathAction = this.mixer.clipAction(animations[indices.death]);
      this.deathAction.setLoop(THREE.LoopOnce);
      this.deathAction.clampWhenFinished = true;

      // 죽음 애니메이션 속도 조정 (멤버 변수 사용)
      const originalDuration = animations[indices.death].duration;
      this.deathAction.setEffectiveTimeScale(
        originalDuration / this.deathAnimationDuration
      );
    }

    // 원래 재질 저장 (피격 효과용)
    this.saveOriginalMaterials();

    // 모든 애니메이션 배열 초기화 (상태 추적용)
    this.allAnimations = [
      this.idleAction,
      this.moveAction,
      this.aimAction,
      this.attackAction,
      this.hitAction,
      this.deathAction,
    ].filter((action) => action !== null); // null이 아닌 것만 포함

    // 기본 idle 애니메이션 재생
    if (this.idleAction) {
      this.idleAction.play();
      this.currentAnimation = this.idleAction;
    }
  }

  // 원래 재질 저장
  saveOriginalMaterials() {
    if (!this.model) return;

    this.model.traverse((child) => {
      if (child.isMesh && child.material) {
        this.originalMaterials.set(child, child.material.clone());
      }
    });
  }

  // 매 프레임 업데이트
  update(deltaTime) {
    if (!this.isLoaded) return;

    // 애니메이션 믹서 업데이트
    if (this.mixer) {
      this.mixer.update(deltaTime);
    }

    // 애니메이션 완료 상태 체크 (attack, hit 애니메이션)
    this.checkAnimationFinished();

    // 피격 상태 업데이트
    this.updateHitState(Date.now() / 1000);

    // 죽음 상태 업데이트
    this.updateDeathState(Date.now() / 1000);
  }

  // 애니메이션 완료 상태 체크 (매 프레임마다 호출)
  checkAnimationFinished() {
    if (!this.currentAnimation) return;

    // 죽고 있거나 죽은 상태면 체크하지 않음
    if (this.isDying || !this.isAlive) return;

    // LoopOnce 애니메이션이고 끝났는지 확인
    const isLoopOnce = this.currentAnimation.loop === THREE.LoopOnce;
    if (!isLoopOnce) return;

    // 애니메이션이 끝났는지 확인 (time이 duration에 도달했는지)
    const clip = this.currentAnimation.getClip();
    const isFinished = this.currentAnimation.time >= clip.duration - 0.05; // 약간의 여유

    if (!isFinished) return;

    // 어떤 애니메이션이 끝났는지 확인하고 처리
    if (this.currentAnimation === this.attackAction) {
      this.onAttackAnimationFinished();
    } else if (this.currentAnimation === this.hitAction) {
      this.onHitAnimationFinished();
    }
  }

  // 피격 상태 업데이트
  updateHitState(currentTime) {
    if (this.isDying) return;

    // 피격 경직 상태 확인
    if (this.isHit && currentTime >= this.hitStunEndTime) {
      this.isHit = false;
    }

    // 피격 깜빡임 효과 확인
    // TODO : 가끔 깜빡임 효과가 안사라지는 경우가 있음.
    if (currentTime >= this.hitFlashEndTime) {
      this.restoreOriginalMaterials();
    }
  }

  // 죽음 상태 업데이트
  updateDeathState(currentTime) {
    if (this.isDying) {
      // 죽음 애니메이션 중에는 회전 고정
      if (this.model) {
        this.model.rotation.x = 0;
        this.model.rotation.z = 0;
      }

      // 죽음 애니메이션이 끝났으면 제거 (멤버 변수 사용)
      const deathDuration = currentTime - this.deathStartTime;
      if (deathDuration >= this.deathAnimationDuration) {
        this.removeFromScene();
      }
    }
  }

  // 데미지 받기
  takeDamage(damage, attackerPosition = null) {
    // isHit 체크를 제거하여 여러 공격자가 동시에 데미지를 줄 수 있도록 함
    if (!this.isAlive || this.isDying) return false;

    this.health -= damage;
    this.health = Math.max(0, this.health);

    const currentTime = Date.now() / 1000;

    // 첫 피격 시에만 hit 상태와 애니메이션 적용
    if (!this.isHit) {
      this.isHit = true;
      // 멤버 변수 사용
      this.hitStunEndTime = currentTime + this.hitStunDuration;

      // 피격 애니메이션 재생
      this.playHitAnimation();
    }

    // 피격 효과는 매번 갱신 (깜빡임 효과 연장)
    this.hitFlashEndTime = currentTime + this.hitFlashDuration;
    this.applyHitEffect();

    // 넉백 효과
    if (attackerPosition) {
      this.applyKnockback(attackerPosition);
    }

    if (this.health <= 0) {
      this.die();
      return true; // 죽음
    }

    return false; // 생존
  }

  // 피격 애니메이션 재생
  playHitAnimation() {
    if (!this.hitAction) return;

    // 현재 애니메이션에서 피격 애니메이션으로 전환
    this.switchAnimation(this.currentAnimation, this.hitAction, 0.1);
  }

  // 피격 효과 (빨간색 깜빡임)
  applyHitEffect() {
    if (!this.model) return;

    this.model.traverse((child) => {
      if (child.isMesh && child.material) {
        const hitMaterial = child.material.clone();
        hitMaterial.color.setHex(0xff4444);
        hitMaterial.emissive.setHex(0x440000);
        child.material = hitMaterial;
      }
    });
  }

  // 원래 재질 복원
  restoreOriginalMaterials() {
    if (!this.model) return;

    this.model.traverse((child) => {
      if (child.isMesh && this.originalMaterials.has(child)) {
        child.material = this.originalMaterials.get(child);
      }
    });
  }

  // 넉백 효과
  applyKnockback(attackerPosition) {
    // 멤버 변수 사용 (config에서 읽되, 없으면 기본값 0.2)
    const knockbackStrength = this.config.KNOCKBACK_STRENGTH || 0.2;
    const direction = new THREE.Vector3()
      .subVectors(this.position, attackerPosition)
      .normalize();

    this.position.add(direction.multiplyScalar(knockbackStrength));
    if (this.model) {
      this.model.position.copy(this.position);
    }
  }

  // 죽음 처리
  die() {
    if (this.isDying) return;

    this.isAlive = false;
    this.health = 0;
    this.isDying = true;
    this.deathStartTime = Date.now() / 1000;

    // 피격 상태 즉시 해제
    this.isHit = false;
    this.restoreOriginalMaterials();

    // 회전 상태 초기화 (죽음 애니메이션 전에 회전을 정리)
    if (this.model) {
      // Y축 회전만 유지하고 X, Z축 회전은 0으로 초기화
      this.model.rotation.x = 0;
      this.model.rotation.z = 0;
    }

    // 죽음 애니메이션 재생
    this.playDeathAnimation();
  }

  // 죽음 애니메이션 재생
  playDeathAnimation() {
    if (!this.deathAction) return;

    // 모든 애니메이션 즉시 정지 후 죽음 애니메이션만 재생
    this.stopAllAnimations();

    // 죽음 애니메이션만 재생
    this.deathAction.reset();
    this.deathAction.setEffectiveWeight(1.0);
    this.deathAction.play();
    this.currentAnimation = this.deathAction;
  }

  // 애니메이션 재생 (이름으로 액션 찾아서 재생)
  playAnimation(animationName) {
    // 애니메이션 이름을 액션 객체로 매핑
    const animationMap = {
      idle: this.idleAction,
      move: this.moveAction,
      walk: this.moveAction, // walk는 move의 별칭
      run: this.moveAction, // run도 move의 별칭
      aim: this.aimAction,
      attack: this.attackAction,
      hit: this.hitAction,
      death: this.deathAction,
    };

    const targetAction = animationMap[animationName];

    if (!targetAction) {
      console.warn(`애니메이션 '${animationName}'을 찾을 수 없습니다.`);
      return;
    }

    // 죽고 있거나 죽은 상태면 죽음 애니메이션만 재생 가능
    if ((this.isDying || !this.isAlive) && animationName !== "death") {
      return;
    }

    // 현재 애니메이션과 다르면 전환
    if (this.currentAnimation !== targetAction) {
      if (this.currentAnimation) {
        this.currentAnimation.fadeOut(0.3);
      }

      targetAction.reset().fadeIn(0.3).play();
      this.currentAnimation = targetAction;
    }
  }

  // 애니메이션 전환
  switchAnimation(fromAction, toAction, duration = 0.3) {
    if (!toAction) return;

    // 죽고 있거나 죽은 상태면 죽음 애니메이션만 전환 가능
    if ((this.isDying || !this.isAlive) && toAction !== this.deathAction) {
      return;
    }

    if (fromAction && fromAction !== toAction) {
      fromAction.fadeOut(duration);
    }

    toAction.reset().fadeIn(duration).play();
    this.currentAnimation = toAction;
  }

  // 모든 애니메이션 즉시 정지
  stopAllAnimations() {
    this.allAnimations.forEach((action) => {
      if (action) {
        action.stop();
        action.setEffectiveWeight(0.0);
      }
    });
  }

  // 공격 애니메이션 재생
  playAttackAnimation() {
    if (!this.attackAction) {
      console.warn(
        `[${this.constructor.name}] 공격 애니메이션을 찾을 수 없습니다.`
      );
      return;
    }

    // 현재 애니메이션에서 공격 애니메이션으로 전환
    this.switchAnimation(this.currentAnimation, this.attackAction, 0.1);
  }

  // 애니메이션 상태 완전 초기화 (게임 재시작용)
  resetAnimationState() {
    // 모든 애니메이션 정지
    this.stopAllAnimations();

    // mixer가 있으면 즉시 업데이트해서 정지 상태 적용
    if (this.mixer) {
      this.mixer.update(0);
    }

    // 회전 상태 초기화
    if (this.model) {
      this.model.rotation.set(0, 0, 0);
    }

    // 애니메이션 상태 추적 시스템 초기화
    this.currentAnimation = null;

    // 움직임 상태 초기화
    this.isMoving = false;

    // 공격 및 피격 상태 초기화
    this.isAttacking = false;
    this.isHit = false;

    // 모든 애니메이션 액션의 가중치를 1.0으로 복원 (T-pose 방지)
    this.allAnimations.forEach((action) => {
      if (action) {
        action.setEffectiveWeight(1.0);
      }
    });

    // idle 애니메이션을 기본 상태로 설정
    if (this.idleAction) {
      this.idleAction.reset();
      this.idleAction.setEffectiveWeight(1.0);
      this.idleAction.play();
      this.currentAnimation = this.idleAction;

      // mixer를 다시 업데이트해서 idle 애니메이션이 즉시 적용되도록 함
      if (this.mixer) {
        this.mixer.update(0);
      }
    }
  }

  // 공격 애니메이션 완료 시 호출되는 메서드
  onAttackAnimationFinished() {
    // 공격 상태 해제
    this.isAttacking = false;

    // 적절한 애니메이션으로 전환
    const targetAction = this.isMoving ? this.moveAction : this.idleAction;
    if (targetAction) {
      this.switchAnimation(this.attackAction, targetAction, 0.2);
    }
  }

  // 피격 애니메이션 완료 시 호출되는 메서드
  onHitAnimationFinished() {
    // 피격 상태 해제
    this.isHit = false;

    // 적절한 애니메이션으로 전환
    const targetAction = this.isMoving ? this.moveAction : this.idleAction;
    if (targetAction) {
      this.switchAnimation(this.hitAction, targetAction, 0.2);
    }
  }

  // 강제 idle 상태로 변경 (게임 특정 상황에서 사용)
  forceIdleState() {
    if (!this.isAlive || this.isDying) return;

    this.isForcedIdle = true;
    this.currentState = "idle";

    // idle 애니메이션 재생
    this.playAnimation("idle");
  }

  // 다른 Entity와의 거리 계산
  getDistanceToEntity(entity) {
    const dx = this.position.x - entity.position.x;
    const dz = this.position.z - entity.position.z;
    return Math.sqrt(dx * dx + dz * dz);
  }

  // 방향으로 회전
  lookAtDirection(direction) {
    if (!this.model || direction.length() === 0) return;

    const targetRotation = Math.atan2(direction.x, direction.z);

    // 부드러운 회전
    let rotationDifference = targetRotation - this.model.rotation.y;
    while (rotationDifference > Math.PI) rotationDifference -= 2 * Math.PI;
    while (rotationDifference < -Math.PI) rotationDifference += 2 * Math.PI;

    this.model.rotation.y += rotationDifference * 0.1;
  }

  // 위치 업데이트
  updatePosition(targetPosition) {
    if (targetPosition) {
      this.position.copy(targetPosition);
      if (this.model) {
        this.model.position.copy(targetPosition);
      }
    } else {
      if (this.model) {
        this.model.position.copy(this.position);
      }
    }
  }

  // 위치 반환
  getPosition() {
    return this.model ? this.model.position : this.position;
  }

  // 로드 상태 확인
  isModelLoaded() {
    return this.isLoaded;
  }

  // 생존 상태 확인
  isAliveCheck() {
    return this.isAlive && !this.isDying;
  }

  // 피격 가능 상태인지 확인
  canTakeDamage() {
    // isHit 체크 제거하여 hit 상태에서도 데미지를 받을 수 있도록 함
    return this.isAlive && this.health > 0 && !this.isDying;
  }

  // 공격 쿨타임 체크
  canAttack(currentTime) {
    return currentTime - this.lastAttackTime >= this.attackCooltime;
  }

  // 공격 범위 내에 있는지 확인
  isInAttackRange(target) {
    const distance = this.getDistanceToEntity(target);
    return distance <= this.attackRange;
  }

  // 체력 정보 반환
  getHealthInfo() {
    return {
      current: this.health,
      max: this.maxHealth,
      percentage: (this.health / this.maxHealth) * 100,
      isAlive: this.isAlive,
      isHit: this.isHit,
    };
  }

  // 환경 오브젝트 참조 설정
  setEnvironmentObjects(objects) {
    this.environmentObjects = objects;
  }

  // 씬에서 제거
  removeFromScene() {
    if (this.model) {
      this.scene.remove(this.model);
    }

    this.isDying = false;
    this.isAlive = false;
  }

  // 정리
  dispose() {
    if (this.model) {
      this.scene.remove(this.model);
    }

    if (this.mixer) {
      this.mixer.stopAllAction();
    }

    // 데미지 숫자들 정리
    this.clearDamageNumbers();
  }

  // ===== 데미지 숫자 표시 시스템 =====

  // 데미지 숫자 표시
  showDamageNumber(damage) {
    if (!this.model) {
      return;
    }

    // 데미지에 따른 색상 결정
    let color = 0xff0000; // 기본 빨간색

    if (damage >= 50) {
      color = 0xff6600; // 주황색 (높은 데미지)
    } else if (damage >= 30) {
      color = 0xffaa00; // 노란색 (중간 데미지)
    } else {
      color = 0xff0000; // 빨간색 (낮은 데미지)
    }

    // 기존 데미지 숫자 개수에 따라 위치 오프셋 계산
    const offsetX = ((this.damageNumbers.length % 3) - 1) * 0.5; // -0.5, 0, 0.5
    const offsetZ = Math.floor(this.damageNumbers.length / 3) * 0.3; // 세로로 쌓기

    // 위치 계산
    const position = this.model.position.clone();
    position.x += offsetX;
    position.z += offsetZ;

    // 데미지 숫자 생성
    const damageNumber = new DamageNumber(this.scene, position, damage, color);

    // 배열에 추가
    this.damageNumbers.push(damageNumber);
  }

  // 데미지 숫자들 업데이트
  updateDamageNumbers(delta) {
    // 카메라 참조 가져오기
    const camera = window.game?.camera;

    // 활성화된 데미지 숫자들 업데이트
    for (let i = this.damageNumbers.length - 1; i >= 0; i--) {
      const damageNumber = this.damageNumbers[i];

      if (damageNumber.isDestroyed()) {
        // 제거된 데미지 숫자는 배열에서 삭제
        this.damageNumbers.splice(i, 1);
      } else {
        // 업데이트
        damageNumber.update(delta);

        // 카메라 방향으로 회전
        if (camera) {
          damageNumber.lookAtCamera(camera);
        }
      }
    }
  }

  // 모든 데미지 숫자 제거
  clearDamageNumbers() {
    this.damageNumbers.forEach((damageNumber) => {
      damageNumber.destroy();
    });
    this.damageNumbers = [];
  }
}

// 전역으로 내보내기
if (typeof module !== "undefined" && module.exports) {
  module.exports = Entity;
} else {
  window.Entity = Entity;
}

// webpack 빌드를 위해 default export 추가
export default Entity;
